import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  input,
  Injector,
  Input,
  OnInit,
  Output,
  Signal,
  HostListener,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MerchantService } from '@galaxy/billing';
import { Frequency } from '@vendasta/galaxy/frequency';
import { GalaxyDataSource } from '@vendasta/galaxy/table';
import { GalaxyColumnDef } from '@vendasta/galaxy/table/src/table.interface';
import { AppPricingApiService, PartnerApiService } from '@vendasta/marketplace-apps';
import { MarketplacePackagesApiService } from '@vendasta/marketplace-packages';
import { BehaviorSubject, combineLatest, distinctUntilChanged, filter, Observable, of, startWith, take } from 'rxjs';
import { map } from 'rxjs/operators';
import { InventoryItemWithPricing } from '../inventory-item';
import { ITEM_PRICING_TABLE_COLUMNS } from './column';
import {
  WholesaleSummary,
  ItemFeeRow,
  ItemPricingTableBaseConfig,
  ItemRow,
  ItemRowChangeEvent,
  ItemRowChangeEventAction,
  PriceSummary,
  PriceTotal,
} from './interface';
import { ItemPricingTableDataSource } from './item-pricing-table.data-source';
import {
  calculateItemRowWholesaleFuturePayments,
  calculateTotalRetailPrice,
  calculateTotalWholesalePrice,
  convertHTMLControlValueToNumber,
  getTotalWholesaleCostDueToDate,
  resetItemFees,
  resetItemVariablePrice,
  updatePercentageManagementFee,
} from './utils';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import {
  BillingFrequencyDialogComponent,
  DialogData,
  BillingFrequencyDialogOutput,
} from '../../billing-frequency-dialog/billing-frequency-dialog.component';
import { galaxyFrequencyToRevenuePeriod } from '../../billing-term-dialog/sales-to-galaxy-frequency.pipe';
import equal from 'fast-deep-equal';
import { WINDOW_WIDTH_BREAKPOINTS } from '../../shared/constants';

@Component({
  selector: 'inventory-ui-item-pricing-table-base',
  templateUrl: './item-pricing-table-base.component.html',
  styleUrls: ['./item-pricing-table-base.component.scss'],
  standalone: false,
})
export class ItemPricingTableBaseComponent implements OnInit {
  config$: Observable<ItemPricingTableBaseConfig>;
  config = input.required<ItemPricingTableBaseConfig>();

  protected _items$$ = new BehaviorSubject<InventoryItemWithPricing[]>([]);
  protected windowSizeIsBelowThreshold$$ = new BehaviorSubject<boolean>(
    window.innerWidth < WINDOW_WIDTH_BREAKPOINTS.tablet_large_minimum,
  );
  productTableViewType$: Observable<'list' | 'grid'> = of('list');

  @Input() set items(value: InventoryItemWithPricing[]) {
    this._items$$.next(value);
  }

  viewOnly = input<boolean>(false);
  viewOnly$ = toObservable(this.viewOnly);
  @Input() showRetailFrequencySuffix = false;

  @Output() updatedItemRow = new EventEmitter<ItemRowChangeEvent>();
  @Output() valid = new EventEmitter<boolean>();
  @Output() wholesaleTotal = new EventEmitter<number>();
  @Output() wholesaleSummaryChanged = new EventEmitter<WholesaleSummary>();

  @HostListener('window:resize')
  onResize(): void {
    this.windowSizeIsBelowThreshold$$.next(window.innerWidth < WINDOW_WIDTH_BREAKPOINTS.tablet_large_minimum);
  }

  private _invalidPriceItems: string[] = [];

  dataSource!: ItemPricingTableDataSource;
  tableDataSource!: GalaxyDataSource<ItemRow>;
  columns: GalaxyColumnDef[] = ITEM_PRICING_TABLE_COLUMNS;
  Frequency = Frequency;
  priceTotals$!: Observable<PriceTotal>;
  showManagementFees = true;

  private injector = inject(Injector);
  private dataMembers!: Signal<ItemRow[]>;
  private wholesaleSummary$!: Observable<PriceSummary>;
  private retailSummary$!: Observable<PriceSummary>;
  public wholesaleSummary!: Signal<PriceSummary>;
  public retailSummary!: Signal<PriceSummary>;

  constructor(
    private dialog: MatDialog,
    private readonly partnerApiService: PartnerApiService,
    private readonly appPricingApiService: AppPricingApiService,
    private readonly merchantService: MerchantService,
    private readonly marketplacePackagesApiService: MarketplacePackagesApiService,
    private readonly destroyRef: DestroyRef,
    private readonly productAnalyticsService: ProductAnalyticsService,
  ) {
    this.config$ = toObservable(this.config);
  }

  ngOnInit(): void {
    if (this.config()?.hideColumns?.length) {
      this.config()?.hideColumns?.forEach((col) => {
        if (col === 'wholesale_price') {
          this.showManagementFees = false;
        }

        this.columns = this.columns.filter((c) => c.id !== col);
      });
    }

    this.dataSource = new ItemPricingTableDataSource(
      this._items$$.asObservable(),
      this.config()?.partnerId,
      this.config()?.marketId,
      this.config()?.businessId ?? '',
      this.shouldExcludeWholesale(),
      this.appPricingApiService,
      this.partnerApiService,
      this.merchantService,
      this.marketplacePackagesApiService,
    );
    this.tableDataSource = new GalaxyDataSource<ItemRow>(this.dataSource);
    this.dataMembers = toSignal(this.tableDataSource.dataMembers$, {
      initialValue: [],
      injector: this.injector,
    });

    this.wholesaleSummary$ = this.getPriceSummaryByContext('wholesale');
    this.retailSummary$ = this.getPriceSummaryByContext('retail');
    this.wholesaleSummary = toSignal(this.wholesaleSummary$, {
      initialValue: { oneTime: 0, monthly: 0, yearly: 0, currency: '' },
      injector: this.injector,
    });
    this.retailSummary = toSignal(this.retailSummary$, {
      initialValue: { oneTime: 0, monthly: 0, yearly: 0, currency: '' },
      injector: this.injector,
    });
    this.priceTotals$ = combineLatest([this.wholesaleSummary$, this.retailSummary$]).pipe(
      map(([wholesale, retail]) => {
        return {
          wholesaleTotal: wholesale,
          retailTotal: retail,
          initialized: true,
        };
      }),
      startWith({
        wholesaleTotal: { oneTime: 0, monthly: 0, yearly: 0, currency: '' },
        retailTotal: { oneTime: 0, monthly: 0, yearly: 0, currency: '' },
        initialized: false,
      }),
    );

    // TODO - clean this up when the ordering workflow is refactored to initialize package pricing
    this.tableDataSource.dataMembers$
      .pipe(
        filter((rows: ItemRow[]) => rows.length > 0),
        take(1),
      )
      .subscribe((rows) => {
        rows.forEach((row) => {
          // explicitly publish changes for packages to initialize pricing
          this.publishChanges(row);
        });
      });
    this.wholesaleSummary$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((wholesale) => {
      this.wholesaleTotal.emit(wholesale.oneTime);
    });

    combineLatest([this.tableDataSource.dataMembers$, this.config$])
      .pipe(
        filter(([rows, _]) => rows.length > 0),
        distinctUntilChanged(
          ([prevRow, prevConfig], [currRow, currConfig]) =>
            equal(prevRow, currRow) &&
            prevConfig?.billingTermsConfig?.defaultStartDate === currConfig?.billingTermsConfig?.defaultStartDate,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(([itemRows, config]) => {
        const contractStartDate = config.billingTermsConfig?.defaultStartDate || new Date();
        const futurePaymentsWithCurrency = calculateItemRowWholesaleFuturePayments(itemRows, contractStartDate);
        const firstPayment = getTotalWholesaleCostDueToDate(itemRows, contractStartDate);
        this.wholesaleSummaryChanged.emit({
          futurePayments: futurePaymentsWithCurrency?.lineItemsMap,
          currencyCode: futurePaymentsWithCurrency?.currencyCode,
          firstPayment,
        } as WholesaleSummary);
      });

    this.productTableViewType$ = combineLatest([
      this.windowSizeIsBelowThreshold$$.asObservable().pipe(distinctUntilChanged()),
      this.viewOnly$.pipe(distinctUntilChanged()),
    ]).pipe(map(([isBelowThreshold, viewOnly]) => (isBelowThreshold && viewOnly ? 'grid' : 'list')));
  }

  shouldExcludeWholesale(): boolean {
    return <boolean>(
      (this.config()?.hideColumns?.length &&
        this.config()?.hideColumns?.includes('wholesale_price') &&
        this.config()?.hidePriceSummary)
    );
  }

  onQuantityChange(changeEvent: Event, item: ItemRow): void {
    const htmlElement = changeEvent.target as HTMLInputElement;
    let quantity = htmlElement.valueAsNumber;
    if (quantity < 1) {
      htmlElement.value = '1';
      quantity = 1;
    }
    this.updateQuantity(item, quantity);
  }

  updateQuantity(item: ItemRow, quantity: number): void {
    item.quantity = quantity;
    if (quantity > 0) {
      item.wholesalePricing.frequencyPrices.forEach((fp) => (fp.total = fp.price * item.quantity));
      item.retailPricing.total = item.retailPricing.price * item.quantity;
    }
    const eventType = quantity > 0 ? 'update' : 'delete';
    this.publishChanges(item, eventType);
  }

  updateRetailPricing(item: ItemRow, value: number) {
    item.retailPricing.price = value;
    item.retailPricing.total = value * item.quantity;
  }

  updateWholesalePricing(item: ItemRow, frequency: Frequency, value: number) {
    const index = item.wholesalePricing.frequencyPrices.findIndex((fp) => fp.frequency === frequency);
    if (index < 0) return;
    let frequencyPrice = item.wholesalePricing.frequencyPrices[index];
    if (!frequencyPrice) return;

    const oldWholeSalePrice = frequencyPrice.price;
    // Clamp the price to the minimum price if it exists
    // Create new object for frequencyPrice to trigger render in table component
    if (frequencyPrice.minimumPrice && frequencyPrice.minimumPrice >= value) {
      item.wholesalePricing.frequencyPrices[index] = { ...frequencyPrice, price: frequencyPrice.minimumPrice };
      frequencyPrice = item.wholesalePricing.frequencyPrices[index];
    } else {
      frequencyPrice.price = value;
    }
    frequencyPrice.total = value * item.quantity;
    updatePercentageManagementFee(item, oldWholeSalePrice);
    this.validateWholesalePrice(item);
  }

  updateSetupFee(item: ItemRow, value: number) {
    item.fees.forEach((fee) => {
      if (fee.type === 'setup') {
        fee.retailAmount = value;
        fee.retailAmountTotal = fee.retailAmount * item.quantity;
      }
    });
  }

  validateWholesalePrice(item: ItemRow) {
    const invalidPrice = item.wholesalePricing.frequencyPrices.every(
      (fp) => fp.minimumPrice && fp.minimumPrice >= fp.price,
    );
    if (invalidPrice) {
      if (!this._invalidPriceItems.includes(item.itemId)) {
        this._invalidPriceItems.push(item.itemId);
        this.valid.emit(this._invalidPriceItems.length === 0);
      }
    } else {
      if (this._invalidPriceItems.includes(item.itemId)) {
        this._invalidPriceItems.splice(this._invalidPriceItems.indexOf(item.itemId), 1);
        this.valid.emit(this._invalidPriceItems.length === 0);
      }
    }
  }

  resetItemPricing(item: ItemRow): void {
    item.retailPricing = { ...item.defaultRetailPricing };
    if (item.wholesalePricing.usesVariablePricing) {
      item.wholesalePricing = resetItemVariablePrice(item);
    }
    item.fees = resetItemFees(item);
    item.billingTerm = undefined;
    this.publishChanges(item);
  }

  onPriceChange(
    changeEvent: Event,
    item: ItemRow,
    priceType: 'wholesale' | 'retail' | 'setupfee',
    frequency: Frequency = Frequency.ONE_TIME,
  ): void {
    const htmlInputValue = convertHTMLControlValueToNumber((<HTMLInputElement>changeEvent.target).value);
    const price = this.convertDollarsToCents(htmlInputValue);
    if (priceType === 'wholesale') this.updateWholesalePricing(item, frequency, price);
    if (priceType === 'retail') this.updateRetailPricing(item, price);
    if (priceType === 'setupfee') this.updateSetupFee(item, price);
    this.publishChanges(item);
  }

  handleFrequencyChange(frequency: Frequency, item: ItemRow): void {
    item.retailPricing.frequency = frequency;
    this.publishChanges(item);
  }

  editRetailBillingFrequency(item: ItemRow): void {
    this.openBillingFrequencyDialog(item).subscribe((output) => {
      if (!output || !output.frequency) {
        return;
      }
      item.retailPricing.frequency = output.frequency;
      item.billingTerm = undefined;
      this.publishChanges(item);
    });
  }

  openBillingFrequencyDialog(itemRow: ItemRow): Observable<BillingFrequencyDialogOutput> {
    return this.dialog
      .open(BillingFrequencyDialogComponent, {
        data: {
          itemId: itemRow.itemId,
          name: itemRow.name,
          editionName: itemRow.type === 'app' ? (itemRow.edition?.name ?? undefined) : undefined,
          iconUrl: itemRow.iconUrl,
          revenueComponent: {
            revenue: itemRow.retailPricing.total,
            period: galaxyFrequencyToRevenuePeriod(itemRow.retailPricing.frequency),
          },
          frequency: itemRow.retailPricing.frequency,
          viewOnly: this.viewOnly(),
        } as DialogData,
        maxWidth: '90vw',
        maxHeight: '90vh',
        width: '500px',
      })
      .afterClosed()
      .pipe(take(1));
  }

  convertDollarsToCents(value: number): number {
    return Math.round(value * 100);
  }

  removeSetupFee(item: ItemRow, fee: ItemFeeRow): void {
    this.updateSetupFee(item, 0);
    item.fees.splice(item.fees.indexOf(fee), 1);
    this.publishChanges(item);
  }

  addSetupFee(itemToUpdate: ItemRow): void {
    itemToUpdate.fees.unshift({
      retailAmount: 0,
      amountType: 'fixed',
      label: 'Setup fee',
      type: 'setup',
      wholesaleAmount: 0,
      frequency: Frequency.ONE_TIME,
    });
  }

  publishChanges(item: ItemRow, action: ItemRowChangeEventAction = 'update', source?: string) {
    if (action === 'edit-billing-term') {
      if (source === 'link') {
        this.productAnalyticsService.trackEvent('billing-term-updated', 'updated-from-link', 'update');
      } else {
        this.productAnalyticsService.trackEvent('billing-term-updated', 'updated-from-menu', 'update');
      }
    }

    this.updatedItemRow.emit({ item, action });
    this.dataSource.refreshItemRows();
  }

  getPriceSummaryByContext = (type: 'wholesale' | 'retail'): Observable<PriceSummary> => {
    return this.tableDataSource.dataMembers$.pipe(
      filter((dataMembers) => dataMembers.length > 0),
      map((dataMembers) => {
        if (type === 'wholesale') return calculateTotalWholesalePrice(dataMembers);
        return calculateTotalRetailPrice(dataMembers);
      }),
      startWith({ oneTime: 0, monthly: 0, yearly: 0, currency: '' }),
    );
  };

  onRemoveItem(item: ItemRow): void {
    this.updateQuantity(item, 0);
  }

  getItems(): ItemRow[] {
    return this.dataMembers();
  }
}
